import { Box, Stack, SxProps, Theme, Tooltip } from "@mui/material";
import { isEmpty, isNil } from "lodash-es";
import { useMemo } from "react";

import { FeatureName } from "@/common/constants/featureName";
import { ROUTE_PATH } from "@/common/constants/routing";
import { EntityHelper } from "@/common/helpers/entity";
import { TextHelper } from "@/common/helpers/text";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { EntityViewVariant } from "@/common/ts/layout";
import { featureManager } from "@/config/features";
import { AppPermission, BaseEntityDto, EntityOriginType } from "@/core/api/generated/v1";
import { pxToRemCustom } from "@/theme";

import AuthorizedElement from "../Auth/AuthorizedElement";
import AppBadge from "../Badge/AppBadge";
import AppChip, { AppChipProps } from "../Chip/AppChip";
import AppIconChip, { AppIconChipProps } from "../Chip/AppIconChip";
import AssetLink from "../Entity/Asset/AssetLink";
import TenantLink from "../Entity/Tenant/TenantLink";
import TenantRequestLink from "../Entity/TenantRequest/TenantRequestLink";
import InlineApiEnumValueList from "../Enum/InlineApiEnumValueList";
import AppIcon from "../Icons/AppIcon";
import AppLink from "../Link/AppLink";
import AppTooltip from "../Tooltip/AppTooltip";
import AppTooltipContent from "../Tooltip/AppTooltipContent";

const defaultDisplayProps = {
  unseenInfo: true,
  importMeta: true,
  origin: true,
  assetMeta: true,
  tenantRequestsMeta: true,
  grantsMeta: true,
};
type DisplayProps = typeof defaultDisplayProps;

export interface EntityChipListProps {
  entity: BaseEntityDto | null | undefined;
  entityViewVariant: EntityViewVariant;
  variant?: "normal" | "compact";
  chipProps?: Partial<AppChipProps>;
  withLinks?: boolean;
  displayProps?: Partial<DisplayProps>;
  sx?: SxProps<Theme>;
}

/** Shows all entity chips. */
export function EntityChipList({
  entity,
  entityViewVariant,
  variant = "normal",
  chipProps,
  displayProps,
  sx,
}: EntityChipListProps) {
  displayProps = { ...defaultDisplayProps, ...displayProps };

  const currentTenant = useCurrentTenant();
  const { hasPermissions } = useAuthorizationInfo();

  const entityMeta = useMemo(() => EntityHelper.getEntityMeta(entity), [entity]);

  const chipPropsComputed = useMemo<Partial<AppChipProps>>(
    () => ({ variant: "outlined", size: "small", color: "default", ...chipProps }),
    [chipProps],
  );

  const iconChipPropsComputed = useMemo<Partial<AppIconChipProps>>(
    () => ({ variant: "outlined", size: "small", color: "default", ...chipProps }),
    [chipProps],
  );

  const components = useMemo(
    () =>
      [
        // Unseen/unread info. Display on list view only to avoid updating unread counter in realtime
        displayProps.unseenInfo &&
        featureManager.isEnabled(FeatureName.ChatMessageUnreadCounter) &&
        entity &&
        entityViewVariant === EntityViewVariant.List &&
        entityMeta?.myUnseen?.totalCount &&
        entityMeta.myUnseen.totalCount > 0 ? (
          <AppBadge
            sx={{
              // extra small badge to fit into table cell
              "& .MuiBadge-badge": {
                ...(variant === "compact"
                  ? {
                      minWidth: 14,
                      width: 14,
                      height: 14,
                      padding: 0,
                      fontSize: pxToRemCustom(12),
                      fontWeight: "normal",
                    }
                  : {}),
              },
            }}
            invisible={variant === "normal"}
            color='secondary'
            overlap={variant === "normal" ? "rectangular" : "circular"}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            badgeContent={<span>{entityMeta.myUnseen.totalCount}</span>}
          >
            <Tooltip
              title={`Unread chat messages in this entity: ${entityMeta.myUnseen.totalCount}.`}
            >
              {variant === "normal" ? (
                <AppChip
                  {...chipPropsComputed}
                  // variant='filled'
                  icon={<AppIcon of='message' />}
                  label={`${entityMeta.myUnseen.totalCount} unread`}
                />
              ) : (
                <AppIconChip
                  {...iconChipPropsComputed}
                  // variant='filled'
                  icon={<AppIcon of='message' />}
                />
              )}
            </Tooltip>
          </AppBadge>
        ) : undefined,

        // Imported
        displayProps.importMeta && entityMeta?.importMeta ? (
          <Tooltip title='This entity was imported.'>
            {variant === "normal" ? (
              <AppChip {...chipPropsComputed} icon={<AppIcon of='import' />} label='Imported' />
            ) : (
              <AppIconChip {...iconChipPropsComputed} icon={<AppIcon of='import' />} />
            )}
          </Tooltip>
        ) : undefined,

        // Origin
        displayProps.origin &&
        entityMeta?.originType &&
        entityMeta.originType === EntityOriginType.External ? (
          <Tooltip title='External. This entity originates from external company or external data.'>
            {variant === "normal" ? (
              <AppChip {...chipPropsComputed} icon={<AppIcon of='external' />} label='External' />
            ) : (
              <AppIconChip {...iconChipPropsComputed} icon={<AppIcon of='external' />} />
            )}
          </Tooltip>
        ) : undefined,

        // Has asset
        displayProps.assetMeta && entityMeta?.assetMeta?.assetId ? (
          <Tooltip
            title={
              <AppTooltipContent>
                <Box>This entity has asset:</Box>
                <Box>
                  <strong>
                    <AssetLink
                      entity={undefined}
                      entityId={entityMeta.assetMeta?.assetId}
                      isInheritStyles
                      inlineProps={{
                        withIcon: false,
                        withTooltip: false,
                      }}
                    />
                  </strong>
                </Box>
              </AppTooltipContent>
            }
          >
            <AppLink
              isEnabled={hasPermissions([AppPermission.FleetAppAccess, AppPermission.AssetRead])}
              to={ROUTE_PATH.ASSET_VIEW(entityMeta.assetMeta?.assetId)}
            >
              {variant === "normal" ? (
                <AppChip {...chipPropsComputed} icon={<AppIcon of='asset' />} label={<>Asset</>} />
              ) : (
                <AppIconChip {...iconChipPropsComputed} icon={<AppIcon of='asset' />} />
              )}
            </AppLink>
          </Tooltip>
        ) : undefined,

        // Added/linked to T2T request
        displayProps.tenantRequestsMeta &&
        entityMeta?.tenantRequestsMeta?.tenantRequestIds &&
        !isEmpty(entityMeta.tenantRequestsMeta?.tenantRequestIds) ? (
          <AppTooltip
            title={
              <AppTooltipContent>
                <Stack spacing={1}>
                  <Box>
                    This entity is linked to{" "}
                    {entityMeta.tenantRequestsMeta?.tenantRequestIds.length} company{" "}
                    {TextHelper.pluralizeManual(
                      "request",
                      entityMeta.tenantRequestsMeta?.tenantRequestIds.length,
                      "requests",
                    )}
                    :
                  </Box>

                  <Stack>
                    {entityMeta.tenantRequestsMeta?.tenantRequestIds.map((tenantRequestId, i) => (
                      <Stack key={i} direction='row' gap={1}>
                        {i + 1}.
                        <TenantRequestLink
                          entity={undefined}
                          entityId={tenantRequestId}
                          isInheritStyles
                          inlineProps={{
                            withIcon: false,
                            withTooltip: false,
                          }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </AppTooltipContent>
            }
          >
            {variant === "normal" ? (
              <AppChip
                {...chipPropsComputed}
                icon={<AppIcon of='tenantRequest' />}
                label={
                  <>
                    {entityMeta.tenantRequestsMeta?.tenantRequestIds?.length} C2C{" "}
                    {TextHelper.pluralizeManual(
                      "request",
                      entityMeta.tenantRequestsMeta?.tenantRequestIds?.length ?? 0,
                      "requests",
                    )}
                  </>
                }
              />
            ) : (
              <AppIconChip {...iconChipPropsComputed} icon={<AppIcon of='tenantRequest' />} />
            )}
          </AppTooltip>
        ) : undefined,

        // Applied result of T2T request
        displayProps.tenantRequestsMeta &&
        entityMeta?.tenantRequestsMeta?.appliedResultOfTenantRequestId ? (
          <Tooltip
            title={
              <AppTooltipContent>
                <Box>This entity is created by applying result of the company request:</Box>
                <Box>
                  <strong>
                    <TenantRequestLink
                      entity={undefined}
                      entityId={entityMeta?.tenantRequestsMeta?.appliedResultOfTenantRequestId}
                      isInheritStyles
                      inlineProps={{
                        withIcon: false,
                        withTooltip: false,
                      }}
                    />
                  </strong>
                </Box>
              </AppTooltipContent>
            }
          >
            {variant === "normal" ? (
              <AppChip
                {...chipPropsComputed}
                icon={<AppIcon of='tenantRequestAppliedResult' />}
                label={<>C2C request result</>}
              />
            ) : (
              <AppIconChip
                {...iconChipPropsComputed}
                icon={<AppIcon of='tenantRequestAppliedResult' />}
              />
            )}
          </Tooltip>
        ) : undefined,

        // Shared via data grant
        displayProps.grantsMeta && entityMeta?.grantsMeta ? (
          <Tooltip
            title={
              <AppTooltipContent>
                <Stack spacing={0}>
                  <Box>
                    {entityMeta.grantsMeta.issuerTenantId === currentTenant?.id && (
                      <Box>
                        <Box>This entity is shared by you via data grant with these companies:</Box>
                        <Stack>
                          {Object.keys(entityMeta.grantsMeta.consumerTenantIdsMap || {}).map(
                            (consumerTenantId, i) => (
                              <Stack key={i} direction='row' gap={1}>
                                {i + 1}.
                                <TenantLink
                                  entity={undefined}
                                  entityId={consumerTenantId}
                                  isInheritStyles
                                  inlineProps={{
                                    withIcon: false,
                                    withTooltip: false,
                                    withModeChip: false,
                                  }}
                                />
                              </Stack>
                            ),
                          )}
                        </Stack>
                      </Box>
                    )}
                    {entityMeta.grantsMeta.consumerTenantIdsMap &&
                      entityMeta.grantsMeta.consumerTenantIdsMap[currentTenant?.id || ""] && (
                        <>
                          This entity is shared with you via data grant by company{" "}
                          <strong>
                            <TenantLink
                              entity={undefined}
                              entityId={entityMeta.grantsMeta.issuerTenantId}
                              isInheritStyles
                              inlineProps={{
                                withIcon: false,
                                withTooltip: false,
                                withModeChip: false,
                              }}
                            />
                          </strong>
                        </>
                      )}
                  </Box>

                  {entityMeta.grantsMeta.tenantPermissions &&
                    entityMeta.grantsMeta.tenantPermissions[currentTenant?.id || ""] && (
                      <Box>
                        Permissions:{" "}
                        <InlineApiEnumValueList
                          type='DataGrantPermission'
                          values={
                            (entityMeta.grantsMeta.tenantPermissions &&
                              entityMeta.grantsMeta.tenantPermissions[currentTenant?.id || ""]) ||
                            []
                          }
                        />
                      </Box>
                    )}
                </Stack>
              </AppTooltipContent>
            }
          >
            <AppLink
              isEnabled={hasPermissions([
                AppPermission.FleetAppAccess,
                AppPermission.DataGrantRead,
              ])}
              to={ROUTE_PATH.DATA_GRANTS({
                grantEntityType: entityMeta.grantsMeta.grantEntityType,
                entityId: entity?.id,
              })}
            >
              {variant === "normal" ? (
                <AppChip {...chipPropsComputed} icon={<AppIcon of='dataGrant' />} label='Shared' />
              ) : (
                <AppIconChip {...iconChipPropsComputed} icon={<AppIcon of='dataGrant' />} />
              )}
            </AppLink>
          </Tooltip>
        ) : undefined,
      ]
        .filter((x) => !isNil(x))
        .map((x) => x!),
    [entity],
  );

  if (!entity || isEmpty(components)) {
    return null;
  }

  return (
    <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
      <Stack
        component='span'
        direction='row'
        sx={{ display: "flex", minWidth: 0, flexWrap: "wrap", gap: 0.5, ...sx }}
      >
        {components.map((component, i) => (
          <span key={i}>{component}</span>
        ))}
      </Stack>
    </AuthorizedElement>
  );
}
